import _ from 'lodash';
import {useCallback, useMemo, useState} from 'react';

import {useMemoizedSelector} from '../../../state/hooks';
import type * as CustomRunColorsTypes from '../../../state/views/customRunColors/types';
import type * as CustomRunNamesTypes from '../../../state/views/customRunNames/types';
import * as GroupSelectionsActions from '../../../state/views/groupSelections/actions';
import type * as GroupSelectionsTypes from '../../../state/views/groupSelections/types';
import * as ViewHooks from '../../../state/views/hooks';
import {type Ref} from '../../../state/views/runSet/types';
import type * as SemanticLegendTypes from '../../../state/views/semanticLegend/types';
import * as TempSelectionsActions from '../../../state/views/tempSelections/actions';
import type * as TempSelectionsTypes from '../../../state/views/tempSelections/types';
import * as Run from '../../../util/runs';
import * as SM from '../../../util/selectionmanager';
import {type WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {type TruncationType} from '../../common/TruncateText/TruncateTextTypes';
import {type JobInfo} from '../../Launch/utilTypes';
import {
  makeGroupSelectionsSelector,
  makeTempSelectionsSelector,
} from '../runSetSelectors';
import {useRunColor} from './useRunColor';

export interface RunSelectorRowHeaderProps {
  className?: string;
  customRunColorsRef: CustomRunColorsTypes.Ref;
  customRunNamesRef: CustomRunNamesTypes.Ref;
  disableRunLink?: boolean;
  entityName: string;
  groupSelectionsRef: GroupSelectionsTypes.Ref;
  isGroup?: boolean; // false if row is individual run, true if it's a group of runs
  isGroupExpanded: boolean;
  isHoveringRow: boolean;
  isLaunchActive?: boolean;
  isLaunchPublicQueueActive?: boolean;
  isSingleMode?: boolean;
  isTableCollapsed?: boolean;
  loadingChildren?: boolean;
  onGroupButtonClick?(): void;
  onSelectRunName?: (runId: string) => void;
  onTempCheckboxClick(): void;
  projectName: string;
  recursionDepth: number;
  row: WBTableRowWithRunSetInfo;
  runSetRef: Ref;
  selectedCount: number;
  selectedRunName?: string;
  semanticLegendRef: SemanticLegendTypes.Ref;
  setLaunchDrawerRow(run: string, jobInfo: JobInfo): void;
  showArtifactCounts?: boolean;
  showLogCounts?: boolean;
  stopRun(id: string): Promise<any>; // from withMutations
  tempSelectionsRef: TempSelectionsTypes.Ref;
  truncationType: TruncationType;
}

export function useRunSelectorRowHeaderProps(props: RunSelectorRowHeaderProps) {
  // Read the parts via reselect-based selectors so derived computations can
  // declare both the part state AND the row as deps. The previous
  // usePartMapped approach only re-invoked its mapper when the part itself
  // changed, which returned a stale result when props.row updated with
  // newly-loaded summary data — the visibility checkbox would not
  // recalculate once a brushed PC axis's summary metric finally landed on
  // the run.
  const groupSelections = useMemoizedSelector(
    makeGroupSelectionsSelector,
    props.groupSelectionsRef
  );
  const tempSelections = useMemoizedSelector(
    makeTempSelectionsSelector,
    props.tempSelectionsRef
  );

  const grouping = groupSelections.grouping;
  const isGroupingEnabled = grouping.length > 0;
  const expandedRowAddresses = groupSelections.expandedRowAddresses;

  const visCheckboxState = useMemo(
    () =>
      SM.getCheckedState(groupSelections, props.row, props.recursionDepth + 1),
    [groupSelections, props.row, props.recursionDepth]
  );

  const runIsInBounds = useMemo(
    () =>
      SM.runIsInBounds(groupSelections, props.row, props.recursionDepth + 1),
    [groupSelections, props.row, props.recursionDepth]
  );

  const tempCheckboxState = useMemo(() => {
    const tempGroupSelections = {
      grouping,
      selections: tempSelections,
      expandedRowAddresses,
    };
    return SM.getCheckedState(
      tempGroupSelections,
      props.row,
      props.recursionDepth + 1
    );
  }, [
    grouping,
    tempSelections,
    expandedRowAddresses,
    props.row,
    props.recursionDepth,
  ]);

  const toggleSelection = ViewHooks.useViewAction(
    props.groupSelectionsRef,
    GroupSelectionsActions.toggleSelection
  );

  const tempSelectToggle = ViewHooks.useViewActionBindAll(
    TempSelectionsActions.selectToggle,
    props.tempSelectionsRef,
    props.runSetRef,
    props.row,
    props.recursionDepth + 1
  );

  const uniqueId = Run.uniqueId(props.row, grouping);
  const groupCounts = props.row.groupCounts ?? [];
  const childGroupCount =
    groupCounts.length >= 2 ? groupCounts[groupCounts.length - 2] : null;
  const isParentGroup = Boolean(props.isGroup && childGroupCount != null);
  // a group color cascades to every run under it; a run row colors only itself
  const affectedRunCount = props.isGroup
    ? (groupCounts[groupCounts.length - 1] ?? 0)
    : 1;
  const {
    clearCustomRunColor,
    customRunColor,
    customRunColorPreviewColors,
    customRunColorSource,
    hasCustomRunColorDescendantColors,
    hasParentInheritedColor,
    isCustomRunColorClearable,
    setCustomRunColor,
  } = useRunColor({
    customRunColorsRef: props.customRunColorsRef,
    entityName: props.entityName,
    grouping,
    isGroup: props.isGroup,
    projectName: props.projectName,
    recursionDepth: props.recursionDepth,
    row: props.row,
    runSetRef: props.runSetRef,
    semanticLegendRef: props.semanticLegendRef,
  });

  const customRunName = ViewHooks.usePartMapped(
    props.customRunNamesRef,
    customRunNames =>
      Run.getCustomRunName(
        props.row.name,
        Boolean(props.isGroup),
        customRunNames
      )
  );

  const groupedBy = grouping[props.recursionDepth];
  const isRunGroup = _.isEqual(groupedBy, {section: 'run', name: 'group'});

  const [stopModalOpen, setStopModalOpen] = useState(false);
  const openStopModal = useCallback(
    () => setStopModalOpen(true),
    [setStopModalOpen]
  );
  const closeStopModal = useCallback(
    () => setStopModalOpen(false),
    [setStopModalOpen]
  );

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const openDeleteModal = useCallback(
    () => setDeleteModalOpen(true),
    [setDeleteModalOpen]
  );
  const closeDeleteModal = useCallback(
    () => setDeleteModalOpen(false),
    [setDeleteModalOpen]
  );

  const {id} = props.row;
  const {stopRun} = props;
  const stopRunCurried = useCallback(() => stopRun(id), [stopRun, id]);

  return {
    closeDeleteModal,
    closeStopModal,
    clearCustomRunColor,
    customRunColor,
    affectedRunCount,
    isCustomRunColorClearable,
    customRunColorSource,
    customRunColorPreviewColors,
    hasCustomRunColorDescendantColors,
    hasParentInheritedColor,
    isGroupingEnabled,
    isParentGroup,
    customRunName,
    setDeleteModalOpen,
    deleteModalOpen,
    groupedBy,
    isRunGroup,
    openDeleteModal,
    openStopModal,
    runIsInBounds,
    setCustomRunColor,
    setLaunchDrawerRow: props.setLaunchDrawerRow,
    stopModalOpen,
    stopRun: stopRunCurried,
    tempCheckboxState,
    tempSelectToggle,
    toggleSelection,
    uniqueId,
    visCheckboxState,
  };
}
